import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
    Alert,
    alpha,
    Box,
    Chip,
    Grid,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { buildApiUrl } from "../../config/api";

function Kpi({ label, value, sub, alert: isAlert }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                bgcolor: isAlert ? (t) => alpha(t.palette.warning.main, 0.06) : undefined,
                borderColor: isAlert ? "warning.main" : undefined,
            }}
        >
            <Typography variant="overline" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="h4" fontWeight={800} color={isAlert ? "warning.main" : undefined}>
                {value}
            </Typography>
            {sub ? (
                <Typography variant="caption" color="text.secondary">
                    {sub}
                </Typography>
            ) : null}
        </Paper>
    );
}

function fmtDate(v) {
    if (!v) return "—";
    try {
        return new Date(v).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return "—";
    }
}

/**
 * Read-only library insights for the director (overview + report summary).
 */
export default function LibraryAnalyticsPanel({ schoolId }) {
    const [overview, setOverview] = useState(null);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        setError("");
        try {
            const [overviewRes, reportRes] = await Promise.all([
                axios.get(buildApiUrl(`/LibraryOverview/${schoolId}`)),
                axios.get(buildApiUrl(`/LibraryReport/${schoolId}`)),
            ]);
            setOverview(overviewRes.data);
            setReport(reportRes.data);
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Could not load library analytics.");
            setOverview(null);
            setReport(null);
        } finally {
            setLoading(false);
        }
    }, [schoolId]);

    useEffect(() => {
        load();
    }, [load]);

    if (!schoolId) return null;

    return (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, mt: 3 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <MenuBookOutlinedIcon color="primary" />
                <Box>
                    <Typography variant="h6" fontWeight={800}>
                        Library & TLMs
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Stock, loans, and low-stock alerts — your librarian manages day-to-day inventory.
                    </Typography>
                </Box>
            </Stack>

            {error ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : null}

            {loading && !overview ? (
                <Skeleton variant="rounded" height={200} />
            ) : (
                <>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6} sm={3}>
                            <Kpi label="Titles" value={overview?.itemCount ?? "—"} sub="Unique resources" />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Kpi
                                label="Available copies"
                                value={overview?.availableCopies ?? "—"}
                                sub={`${overview?.onLoan ?? 0} on loan`}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Kpi
                                label="Overdue loans"
                                value={overview?.overdueLoans ?? "—"}
                                sub={`${overview?.activeLoans ?? 0} active`}
                                alert={Number(overview?.overdueLoans) > 0}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Kpi
                                label="Low stock"
                                value={overview?.lowStockCount ?? "—"}
                                sub="At or below threshold"
                                alert={Number(overview?.lowStockCount) > 0}
                            />
                        </Grid>
                    </Grid>

                    {overview?.lowStockCount > 0 ? (
                        <Alert severity="warning" icon={<WarningAmberOutlinedIcon />} sx={{ mb: 2, borderRadius: 2 }}>
                            {overview.lowStockCount} item{overview.lowStockCount === 1 ? "" : "s"} need reordering.
                        </Alert>
                    ) : null}

                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        Stock by category
                    </Typography>
                    <TableContainer sx={{ mb: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Category</TableCell>
                                    <TableCell align="right">Titles</TableCell>
                                    <TableCell align="right">Copies</TableCell>
                                    <TableCell align="right">Available</TableCell>
                                    <TableCell align="right">On loan</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(report?.categorySummary || []).length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>
                                            No library items recorded yet.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    (report?.categorySummary || []).map((row) => (
                                        <TableRow key={row.category}>
                                            <TableCell>{row.category}</TableCell>
                                            <TableCell align="right">{row.titles}</TableCell>
                                            <TableCell align="right">{row.totalCopies}</TableCell>
                                            <TableCell align="right">{row.available}</TableCell>
                                            <TableCell align="right">{row.onLoan}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        Recent borrowing activity
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell>Borrower</TableCell>
                                    <TableCell>Due</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(report?.recentBorrowings || []).length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>
                                            No recent loans.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    (report?.recentBorrowings || []).slice(0, 8).map((row) => {
                                        const id = row._id || row.id;
                                        const status = row.status || "Active";
                                        return (
                                            <TableRow key={id} hover>
                                                <TableCell sx={{ fontWeight: 600 }}>{row.item?.title || "—"}</TableCell>
                                                <TableCell>{row.borrowerName || "—"}</TableCell>
                                                <TableCell>{fmtDate(row.dueDate)}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={status}
                                                        color={
                                                            status === "Overdue"
                                                                ? "error"
                                                                : status === "Returned"
                                                                  ? "default"
                                                                  : "primary"
                                                        }
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {report?.generatedAt ? (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                            Report snapshot: {fmtDate(report.generatedAt)}
                        </Typography>
                    ) : null}
                </>
            )}
        </Paper>
    );
}
